"use client";

import TextInput from "@/app/components/atoms/TextInput";
import PasswordInput from "@/app/components/atoms/PasswordInput";
import { getFieldType, normaliseField } from "@/lib/form-normalise";
import type { FormFieldConfig } from "@/app/components/types";

interface FieldRendererProps {
  /** Field configuration defining type, label, and placeholder. */
  field: FormFieldConfig;
  /** Current value for the field. */
  value: string | boolean;
  /** Callback fired when the field value changes. */
  onChange: (name: string, value: string | boolean) => void;
  /** Optional validation error message displayed below the field. */
  error?: string | null;
  /** Called after blur normalisation with any inline error (or null to clear). */
  onBlurError?: (name: string, error: string | null) => void;
}

/**
 * @component FieldRenderer
 * @description Renders the appropriate input control for a given field config.
 * Supports text-based inputs via TextInput, password inputs with a show/hide toggle,
 * and checkboxes natively. Used by DynamicForm to render fields from a JSON config.
 * @param field - The field configuration object.
 * @param value - The current field value.
 * @param onChange - Callback when the value changes.
 * @param error - Optional error message shown below the field.
 * @returns The appropriate input control for the field type.
 */
export default function FieldRenderer({
  field,
  value,
  onChange,
  error,
  onBlurError,
}: FieldRendererProps) {
  const handleBlur = () => {
    if (field.disabled || field.readOnly || field.type === "checkbox" || field.type === "password") return;
    const raw = String(value ?? "");
    const { value: normalised, error: normError } = normaliseField(getFieldType(field.name), raw);
    if (normalised !== raw) onChange(field.name, normalised);
    onBlurError?.(field.name, normError ?? null);
  };
  if (field.readOnly) {
    return (
      <div className="flex flex-col items-baseline gap-2">
        <span className="text-label text-muted-text">
          {field.label}
        </span>
        <span className="text-body-base font-normal text-muted-text">
          {value as string}
        </span>
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          name={field.name}
          checked={value as boolean}
          onChange={(e) => onChange(field.name, e.target.checked)}
          className="w-4 h-4 accent-schooner cursor-pointer"
        />
        <span className="text-label text-charcoal">
          {field.label}
        </span>
      </label>
    );
  }

  if (field.type === "password") {
    return (
      <PasswordInput
        id={field.name}
        label={field.label || undefined}
        value={value as string}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder}
        required={field.required}
        autoComplete={field.autoComplete}
        invalid={!!error}
        error={error}
        errorId={error ? `${field.name}-error` : undefined}
      />
    );
  }

  return (
    <TextInput
      id={field.name}
      type={field.type}
      label={field.label || undefined}
      placeholder={field.placeholder}
      value={value as string}
      onChange={(e) => onChange(field.name, e.target.value)}
      onBlur={handleBlur}
      required={field.required}
      readOnly={field.readOnly}
      disabled={field.disabled}
      autoFocus={field.autoFocus}
      error={error}
      errorId={error ? `${field.name}-error` : undefined}
    />
  );
}
